import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Fields, reduxForm, SubmissionError } from 'redux-form';

import Button from 'components/common/Button';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import FormError from 'components/common/FormError';

import {
  addPatientDocumentResource,
  removePatientDocumentResource,
} from 'redux/modules/patientsDocuments';

import {
  requestSearchDocumentCategories, requestFetchDocumentCategories,
} from 'redux/modules/documentCategories';
import { accountLoginFail } from 'redux/modules/account';

const styles = () => ({
  buttons: {
    marginTop: 20,
  },
  buttonsCreate: {
    textAlign: 'right',
  },
  button: {
    '& + $button': {
      marginLeft: 20,
    },
  },
  formError: {
    marginBottom: 16,
  },
  checkboxField: {
    marginBottom: 8,
  },
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function onChange(values, dispatch, props) {
  if (values['Selected Document Category']) {
    if (!values['Document Category']
      || values['Document Category'].findIndex(item =>
        item.value === values['Selected Document Category'].value) === -1) {
      props.array.push('Document Category', values['Selected Document Category']);
    }
    props.onFormChange();
    props.clearFields(false, false, 'Selected Document Category');
  }
}

function onSubmit(values, dispatch, props) {
  if (props.pristine) {
    return Promise.reject();
  }
  const addedDocumentCategory = values['Document Category'].filter(item =>
    props.initialValues['Document Category'].filter(initialItem => initialItem.value === item.value).length === 0);
  const removedDocumentCategory = props.initialValues['Document Category'].filter(initialItem =>
    values['Document Category'].filter(item => item.value === initialItem.value).length === 0);
  return new Promise((resolve, reject) => {
    if (addedDocumentCategory.length > 0) {
      dispatch(addPatientDocumentResource({
        values: {
          ID: values.ID,
          'Document Category ID': addedDocumentCategory.map(item => item.value),
        },
        resolve,
        reject,
      }));
    } else {
      resolve();
    }
  })
    .then(() => new Promise((resolve, reject) => {
      if (removedDocumentCategory.length > 0) {
        dispatch(removePatientDocumentResource({
          values: {
            ID: values.ID,
            'Document Category ID': removedDocumentCategory.map(item => item.value),
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    }))
    .then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
}

function onSubmitSuccess(values, dispatch, props) {
  if (!props.ID) {
    props.reset();
  }
}

const DocumentsForm = ({
  handleSubmit,
  submit,
  change,
  error,
  initialValues,
  onCancelButtonClick,
  onSubmitButtonBlur,
  textFieldRef,
  pristine,
  classes,
  actions,
  onFormChange,
  onSubmitButtonClick,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
  >
    {error &&
      <FormError className={classes.formError}>
        {error}
      </FormError>
    }
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Fields
          names={[
            'Document Category Name',
            'Document Category',
          ]}
          autosuggestFieldName="Document Category Name"
          fieldArrayName="Document Category"
          placeholder="-Select Category-"
          component={MultiselectAutosuggestField}
          renderSuggestionContent={suggestion =>
            suggestion.Name}
          renderInputContent={suggestion =>
            suggestion.Name}
          onSuggestionSelected={(suggestion) => {
            change('Selected Document Category', {
              value: suggestion.ID,
              label: suggestion.Name,
            });
            return '';
          }}
          fetchSuggestions={({ value, limit, offset, fieldArrayValue }) => (
            value.length > 0 || (fieldArrayValue && fieldArrayValue.length > 0)
              ? requestSearchDocumentCategories({
                filters: [
                  ...value.length > 0
                    ? [{
                      name: 'Name',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...fieldArrayValue && fieldArrayValue.length > 0
                    ? fieldArrayValue.map(item => ({
                      name: 'ID',
                      operation: 'ne',
                      val: item.value,
                    }))
                    : [],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchDocumentCategories({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          keepOpenAfterSelection
          textFieldRef={textFieldRef}
          onSearchInputChange={() => {
            onFormChange();
          }}
        />
      </Grid>
    </Grid>
    <div
      className={classNames(classes.buttons, {
        [classes.buttonsCreate]: !initialValues.ID,
      })}
    >
      {initialValues.ID &&
        <Button
          className={classes.button}
          variant="raised"
          onClick={onCancelButtonClick}
        >
          Cancel
        </Button>
      }
      <Button
        className={classes.button}
        variant="raised"
        color="primary"
        onClick={() => {
          if (!pristine) {
            submit();
          } else {
            onSubmitButtonClick();
          }
        }}
        {...!initialValues.ID ? {
          onBlur: onSubmitButtonBlur,
        } : {}}
      >
        Save
      </Button>
    </div>
  </form>
);

DocumentsForm.propTypes = {
  pristine: PropTypes.bool.isRequired,
  onSubmitButtonClick: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  onCancelButtonClick: PropTypes.func,
  initialValues: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onSubmitButtonBlur: PropTypes.func,
  textFieldRef: PropTypes.func,
  onFormChange: PropTypes.func,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    buttons: PropTypes.string.isRequired,
    buttonsCreate: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    formError: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

DocumentsForm.defaultProps = {
  onCancelButtonClick: () => {},
  initialValues: {},
  error: null,
  onSubmitButtonBlur: () => {},
  textFieldRef: () => {},
  onSubmitButtonClick: () => {},
  onFormChange: () => {},
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  onSubmit,
  onChange,
  onSubmitSuccess,
})(withStyles(styles)(DocumentsForm)));



// WEBPACK FOOTER //
// ./src/components/patient/PatientProfile/Documents/DocumentsForm.js